"use client";

import { useEffect, useState } from "react";

import clsx from "clsx";

import { Card, CardContent } from "@repo/ui/components/ui/card";
import type { CarouselApi } from "@repo/ui/components/ui/carousel";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@repo/ui/components/ui/carousel";

interface CarouselListProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  keyExtractor: (item: T) => string | number;
  itemClass?: string;
  className?: string;
  carouselContentClass?: string;
}

export const CommonCarousel = <T,>({
  items,
  renderItem,
  keyExtractor,
  itemClass = "basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4",
  className = "",
  carouselContentClass = "",
}: Readonly<CarouselListProps<T>>) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    const onInit = () => {
      const snaps = api.scrollSnapList();
      setScrollSnaps((prev) => {
        if (prev.length === snaps.length) return prev;
        return snaps;
      });
      setCurrent(api.selectedScrollSnap());
    };

    onInit();

    api.on("select", onSelect);
    api.on("reInit", onInit);

    return () => {
      api.off("select", onSelect);
      api.off("reInit", onInit);
    };
  }, [api]);

  const showDots = scrollSnaps.length > 1;

  return (
    <Carousel
      setApi={setApi}
      opts={{ align: "start", watchDrag: showDots }}
      className={clsx("mx-auto w-full max-w-6xl", className)}
    >
      <CarouselContent className={carouselContentClass}>
        {items.map((item) => (
          <CarouselItem key={keyExtractor(item)} className={itemClass}>
            <Card className="h-full p-0">
              <CardContent className="h-full p-0">{renderItem(item)}</CardContent>
            </Card>
          </CarouselItem>
        ))}
      </CarouselContent>
      {showDots && <CarouselPrevious />}
      {showDots && <CarouselNext />}
      {/* {DOT PAGINATION} */}
      {showDots && (
        <div className="flex justify-center mt-4 gap-[19px]">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={clsx(
                "h-[9px] w-[9px] rounded-full transition-colors duration-300 ease-in-out focus:outline-none",
                {
                  "bg-gray-300": index !== current,
                  "bg-brand-cyan": index === current,
                },
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </Carousel>
  );
};
